import { useCallback, useEffect, useState } from 'react';
import { Loader } from '../../../components';
import { EditSVG, InfoSVG } from '../../../components/svgs';
import { BUTTON_CONSTANTS, MARKETPLACE_SETTINGS_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { showToast } from '../../../services/toast.service';
import { getMarketplaceCommissionById, updateMarketplaceCommission } from '../../../api/marketplaceCommissionApi';
import { MarketplaceCommission } from '../../../types/interfaces/marketPlace.interfaces';
import './marketplaceSettings.scss';

const PERCENTAGE_MIN = 0;
const PERCENTAGE_MAX = 100;

const DEFAULT_COMMISSION: MarketplaceCommission = {
  marketPlaceCommissionId: 0,
  commissionTitle: 'Default Commission',
  commissionDescription: '',
  commissionType: 'Percentage',
  commissionValue: 0,
};

const COMMISSION_ID = 1;

function isPercentageValid(value: number): boolean {
  if (Number.isNaN(value)) return false;
  if (value < PERCENTAGE_MIN || value > PERCENTAGE_MAX) return false;
  const rounded = Math.round(value * 100) / 100;
  return rounded === value;
}

function formatCommissionDisplay(value: number): string {
  return `${Number(value).toFixed(2)}%`;
}

function buildUpdatePayload(commission: MarketplaceCommission, editedCommissionValue: number): MarketplaceCommission {
  const valueRounded = Math.round(editedCommissionValue * 100) / 100;
  return {
    marketPlaceCommissionId: commission.marketPlaceCommissionId,
    commissionTitle: commission.commissionTitle,
    commissionDescription: commission.commissionDescription ?? '',
    commissionType: 'Percentage',
    commissionValue: valueRounded,
  };
}

const MarketplaceSettings = (): JSX.Element => {
  const [commission, setCommission] = useState<MarketplaceCommission | null>(null);
  const [commissionValue, setCommissionValue] = useState<number>(0);
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [loadingCommission, setLoadingCommission] = useState(true);
  const [savingCommission, setSavingCommission] = useState(false);

  const displayCommission = commission ?? DEFAULT_COMMISSION;

  const fetchCommission = useCallback(() => {
    setLoadingCommission(true);
    getMarketplaceCommissionById(COMMISSION_ID)
      .then(response => {
        const data = response.data;
        setCommission(data ?? null);
        setCommissionValue(data?.commissionValue ?? 0);
      })
      .catch(() => {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setCommission(null);
        setCommissionValue(0);
      })
      .finally(() => setLoadingCommission(false));
  }, []);

  useEffect(() => {
    fetchCommission();
  }, [fetchCommission]);

  const handleEdit = useCallback(() => {
    setCommissionValue(displayCommission.commissionValue);
    setIsEditingCommission(true);
  }, [displayCommission.commissionValue]);

  const handleCancel = useCallback(() => {
    setCommissionValue(commission?.commissionValue ?? 0);
    setIsEditingCommission(false);
  }, [commission?.commissionValue]);

  const handleSave = useCallback(async () => {
    if (!commission?.marketPlaceCommissionId) {
      showToast.error(toastMessages.COMMISSION_NOT_LOADED);
      return;
    }
    if (!isPercentageValid(commissionValue)) return;
    setSavingCommission(true);
    const payload = buildUpdatePayload(commission, commissionValue);
    const id = commission.marketPlaceCommissionId;
    try {
      const response = await updateMarketplaceCommission(id, payload);
      const updated = response.data;
      if (updated) {
        setCommission(updated);
        setCommissionValue(updated.commissionValue);
      } else {
        setCommission(prev => (prev ? { ...prev, commissionValue: payload.commissionValue } : prev));
        setCommissionValue(payload.commissionValue);
      }
      setIsEditingCommission(false);
      showToast.success(toastMessages.SETTINGS_UPDATED);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setSavingCommission(false);
    }
  }, [commission, commissionValue]);

  const isSaveDisabled = savingCommission || !commission?.marketPlaceCommissionId || !isPercentageValid(commissionValue);

  return (
    <div className="marketplace-settings">
      <div className="marketplace-settings__panel">
        <div className="marketplace-settings__header">
          <h1 className="marketplace-settings__title">{PAGE_HEADER_CONSTANTS.MARKETPLACE_SETTINGS}</h1>
        </div>

        <div className="marketplace-settings__section">
          <div className="marketplace-settings__section-header">
            <h2 className="marketplace-settings__section-title">{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_CONFIGURATION}</h2>
            {!isEditingCommission && (
              <button
                type="button"
                className="marketplace-settings__edit"
                onClick={handleEdit}
                aria-label={BUTTON_CONSTANTS.EDIT}
                disabled={loadingCommission || savingCommission}>
                <EditSVG />
              </button>
            )}
          </div>

          <div className="marketplace-settings__section-body">
            {loadingCommission || savingCommission ? (
              <div className="marketplace-settings__section-loader">
                <Loader loaderSize="small" />
              </div>
            ) : (
              <>
                <div className="marketplace-settings__commission-column">
                  <p className="marketplace-settings__description">
                    {MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_CONFIGURATION_DESCRIPTION}
                  </p>
                  <div className="marketplace-settings__commission-section">
                    <label className="marketplace-settings__label">{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_RATE_LABEL}</label>
                    {isEditingCommission ? (
                      <div className="marketplace-settings__edit-actions">
                        <input
                          type="number"
                          min={PERCENTAGE_MIN}
                          max={PERCENTAGE_MAX}
                          step={1}
                          className="marketplace-settings__input"
                          value={commissionValue}
                          onChange={e => setCommissionValue(Number(e.target.value) || 0)}
                          disabled={savingCommission}
                        />
                        <div className="marketplace-settings__buttons">
                          <button
                            type="button"
                            className="button secondary small"
                            onClick={handleCancel}
                            disabled={savingCommission}>
                            {BUTTON_CONSTANTS.CANCEL}
                          </button>
                          <button type="button" className="button green small" onClick={handleSave} disabled={isSaveDisabled}>
                            {BUTTON_CONSTANTS.SAVE}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="marketplace-settings__value">{formatCommissionDisplay(displayCommission.commissionValue)}</p>
                    )}
                    <p className="marketplace-settings__helper">{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_HELPER_TEXT}</p>
                  </div>
                </div>

                <div className="marketplace-settings__rules-panel">
                  <h3 className="marketplace-settings__rules-title">
                    <InfoSVG size="18" />
                    {MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_APPLICATION_RULES_TITLE}
                  </h3>
                  <ul className="marketplace-settings__rules-list">
                    <li>{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_RULE_PLATFORM_WIDE}</li>
                    <li>{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_RULE_NEW_ORDERS}</li>
                    <li>{MARKETPLACE_SETTINGS_CONSTANTS.COMMISSION_RULE_NO_RETROACTIVE}</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceSettings;
